import { useQuery } from 'react-query';
import { predictionArbitrageManagerAxiosApi } from 'src/Config/axiosApi';

/**
 * Money side of the per-opener-group LIVE summary. All values come straight
 * from the prediction-arbitrage-manager — no client-side PnL/fee math. The
 * descalce + contract counts are delivered separately over the socket
 * (`useGroupLivePosition`); this hook owns only the money figures.
 *
 * GET `${VITE_PREDICTION_ARBITRAGE_MANAGER_URL}/opener-groups/:id/live-summary`
 * (reuses `predictionArbitrageManagerAxiosApi`, the same instance the group
 * finish hooks use for the prediction-arbitrage-manager).
 */
export interface GroupLiveSummaryContracts {
  kalshi: number;
  poly: number;
  total: number;
}

export interface GroupLiveSummary {
  groupId: string;
  nominalUsd: number;
  spendUsd: number;
  feesUsd: number;
  contractsBought: GroupLiveSummaryContracts;
  matchedContracts: number;
  livePnlUsd: number;
  pairsCounted: number;
  computedAt: number;
  stale: boolean;
}

export const groupLiveSummaryQueryKey = (groupId: string) => [
  'group-live-summary',
  groupId
];

export async function fetchGroupLiveSummary(
  groupId: string
): Promise<GroupLiveSummary> {
  const { data } =
    await predictionArbitrageManagerAxiosApi.get<GroupLiveSummary>(
      `/opener-groups/${groupId}/live-summary`
    );
  return data;
}

/**
 * Refreshes on a fixed ~5s interval while a group is selected — matched to the
 * backend's 5s cache TTL, so this never fetches faster than the server
 * recomputes. A fixed interval (not a signal-triggered debounce) guarantees the
 * money keeps advancing DURING active trading; a trailing debounce keyed on the
 * live socket signal would keep resetting and never fire while fills flow.
 */
export function useGroupLiveSummary(groupId: string | null | undefined) {
  const enabled = !!groupId;
  return useQuery<GroupLiveSummary>(
    groupLiveSummaryQueryKey(groupId ?? ''),
    () => fetchGroupLiveSummary(groupId as string),
    {
      enabled,
      keepPreviousData: true,
      staleTime: 4_000,
      refetchInterval: enabled ? 5_000 : false
    }
  );
}
